import { Button } from "@/components/core/buttons/button";
import { DynamicTagsInput } from "@/components/core/forms/dynamic-tags-input";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { type CaseActivity, type CaseActivityNotifySchema, activityNotifySchema } from "@/features/case-evaluation/types/activities";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Alert from "../core/alert";
import { Input } from "../core/forms/input";
import { Textarea } from "../core/forms/textarea";

export default function CaseActivityNotifyModal({
	data,
	handleSubmit,
}: {
	data?: CaseActivity;
	handleSubmit: values;
}) {
	const { closeModal } = useModalControl();
	const [error, setError] = useState(null);
	const [tags, setTags] = useState<string[]>([]);

	const form = useForm<CaseActivityNotifySchema>({
		resolver: zodResolver(activityNotifySchema),
		mode: "onChange",
		defaultValues: {
			id: data?.id,
			password: "",
			external_emails: [],
		},
	});

	useEffect(() => {
		form.setValue("external_emails", tags);
	}, [tags, form]);

	return (
		<Form {...form}>
			<form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full gap-4">
					<div className="flex flex-col w-full rounded-md border border-border/50">
						<div className="flex flex-col items-center justify-between w-full py-3 px-4 gap-3">
							<div className="grid grid-cols-12 w-full py-1 gap-x-4 gap-y-3">
								<div className="col-span-12 w-full">
									<Alert type="info" allowClose={false} title="Send Email Notification">
										Are you sure you want to notify the parties involved in this case? This action will send notifications to all parties associated with this
										case.
									</Alert>
								</div>

								<div className="col-span-12 w-full">
									<FormField
										control={form.control}
										name="external_email"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel>External Recipient</FormLabel>
													<FormDescription>Enter email then press comma or enter to add it to the recipients list.</FormDescription>
												</div>
												<FormGroup className="w-full">
													<div className="w-full">
														<DynamicTagsInput placeholder="Add Recipient" value={tags} onChange={setTags} />
													</div>
													{/* <Input autoComplete="off" type="external_email" {...field} /> */}
												</FormGroup>
												{error && <FormMessage>{error}</FormMessage>}
											</FormItem>
										)}
									/>
								</div>

								<div className="col-span-12 w-full">
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem>
												<div className="flex flex-col w-full">
													<FormLabel required>Pin Code</FormLabel>
													<FormDescription>Enter your pin code to confirm and proceed with the submission.</FormDescription>
												</div>
												<FormGroup>
													<Input autoComplete="off" type="password" {...field} />
												</FormGroup>
												{error && <FormMessage>{error}</FormMessage>}
											</FormItem>
										)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-row justify-between w-full pt-4">
					<Button
						className=""
						variant="ghost"
						type="button"
						onClick={() => {
							closeModal();
						}}
					>
						Cancel
					</Button>
					<Button className="" loading={form.formState.isSubmitting}>
						Confirm
					</Button>
				</div>
			</form>
		</Form>
	);
}
